/** Shared UK train driving licence field parsing from OCR plain text (browser + server). */

function normalizeIsoDate(raw: unknown): string | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const compact8 = s.match(/^(\d{8})$/);
  if (compact8) {
    const v = compact8[1];
    const dd = v.slice(0, 2);
    const mm = v.slice(2, 4);
    const yyyy = v.slice(4, 8);
    const iso = `${yyyy}-${mm}-${dd}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = s.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})$/);
  if (dmy) {
    const dd = dmy[1].padStart(2, "0");
    const mm = dmy[2].padStart(2, "0");
    const yyyy = dmy[3];
    return `${yyyy}-${mm}-${dd}`;
  }
  const dmyShortYear = s.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2})$/);
  if (dmyShortYear) {
    const dd = dmyShortYear[1].padStart(2, "0");
    const mm = dmyShortYear[2].padStart(2, "0");
    const yy = Number(dmyShortYear[3]);
    const yyyy = yy >= 70 ? 1900 + yy : 2000 + yy;
    return `${yyyy}-${mm}-${dd}`;
  }
  return null;
}

function normalizeOcrText(raw: string): string {
  return String(raw || "")
    .replace(/[|]/g, "I")
    .replace(/[Oo]/g, "0")
    .replace(/[Il]/g, "1")
    .replace(/\s+/g, " ")
    .trim();
}

function extractDateNearLabel(text: string, labelPattern: RegExp): string | null {
  const labelMatch = text.match(labelPattern);
  if (!labelMatch || labelMatch.index == null) return null;
  const tail = text.slice(labelMatch.index, Math.min(text.length, labelMatch.index + 120));
  const dateMatch = tail.match(/(\d{1,2}[\/.\-]\d{1,2}[\/.\-]\d{2,4}|\d{4}[\/.\-]\d{1,2}[\/.\-]\d{1,2})/);
  return dateMatch ? normalizeIsoDate(dateMatch[1]) : null;
}

function splitOcrLines(rawText: string): string[] {
  return String(rawText || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

function readFieldValueFromNumberedLines(lines: string[], field: "4a" | "4b" | "4d" | "5"): string | null {
  const variantsByField: Record<"4a" | "4b" | "4d" | "5", string[]> = {
    "4a": ["4a", "4 a", "da"],
    "4b": ["4b", "4 b", "48"],
    "4d": ["4d", "4 d"],
    "5": ["5", "s"],
  };
  const variantExpr = variantsByField[field]
    .map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s*"))
    .join("|");
  const inline = new RegExp(`^(?:${variantExpr})\\s*[\\)\\].:,-]?\\s*(.+)$`, "i");
  const onlyLabel = new RegExp(`^(?:${variantExpr})\\s*[\\)\\].:,-]?\\s*$`, "i");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const mInline = line.match(inline);
    if (mInline && mInline[1]) {
      const v = mInline[1].trim();
      if (v) return v;
    }
    if (onlyLabel.test(line) && i + 1 < lines.length) {
      const next = lines[i + 1].trim();
      if (next) return next;
      if (i + 2 < lines.length) {
        const next2 = lines[i + 2].trim();
        if (next2) return next2;
      }
    }
  }
  return null;
}

export function extractUk7120LicenceNumber(rawText: string): string | null {
  const upper = String(rawText || "").toUpperCase();
  const compactMatches = Array.from(upper.matchAll(/\bUK7120\d{6}\b/g)).map((m) => m[0]);
  const unique = [...new Set(compactMatches)];
  if (unique.length >= 1) return unique[0];
  const spaced = upper
    .replace(/UK\s*7\s*[I1L]\s*2\s*0/g, "UK7120")
    .match(/UK7120(?:[\s\-._]*\d){6}/);
  if (spaced) {
    const n = spaced[0].replace(/[^A-Z0-9]/g, "");
    if (/^UK7120\d{6}$/.test(n)) return n;
  }
  return null;
}

export function normalizeVerifiedTrainLicenceNumber(raw: unknown): string | null {
  const compact = String(raw ?? "")
    .replace(/\s+/g, "")
    .toUpperCase();
  if (!/^UK7120\d{6}$/.test(compact)) return null;
  return compact;
}

function extractLicenceNumberFromField5(lines: string[], rawText: string): string | null {
  const label5Inline = /(?:^|\s)(?:5|s)\s*[)\].:,-]?\s*(.+)$/i;
  const label5Only = /^(?:5|s)\s*[)\].:,-]?\s*$/i;
  const stopLabel = /^(?:4a|4\s*a|4b|4\s*b|4d|4\s*d|6|7|8|9)\b/i;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const inline = line.match(label5Inline);
    if (inline && inline[1]) {
      const direct = normalizeLicenceNumber(inline[1]);
      if (direct) return direct;
    }
    if (label5Only.test(line)) {
      const buf: string[] = [];
      for (let j = i + 1; j < Math.min(lines.length, i + 4); j++) {
        const next = lines[j].trim();
        if (!next || stopLabel.test(next)) break;
        buf.push(next);
      }
      const joined = normalizeLicenceNumber(buf.join(" "));
      if (joined) return joined;
    }
  }

  const between = String(rawText || "").match(
    /(?:^|\n)\s*(?:5|S)\s*[)\].:,-]?\s*([\s\S]{0,120}?)(?=(?:\n\s*(?:4a|4\s*a|4b|4\s*b|4d|4\s*d|6|7|8|9)\b)|$)/i,
  );
  if (between && between[1]) {
    const fromBlock = normalizeLicenceNumber(between[1]);
    if (fromBlock) return fromBlock;
  }

  return null;
}

function firstDateInText(raw: string | null): string | null {
  const s = String(raw || "");
  const m = s.match(/(\d{1,2}[\/.\-]\d{1,2}[\/.\-]\d{2,4}|\d{4}[\/.\-]\d{1,2}[\/.\-]\d{1,2})/);
  return m ? normalizeIsoDate(m[1]) : null;
}

function normalizeLicenceNumber(raw: string | null): string | null {
  const s = String(raw || "")
    .replace(/\s+/g, " ")
    .replace(/^[^A-Z0-9]+/i, "")
    .trim();
  if (!s) return null;
  const compact = s.replace(/[^A-Z0-9]/gi, "").toUpperCase();
  if (compact.length < 5) return null;
  const strict = compact.match(/^([A-Z]{0,4}\d{4,14})/);
  if (strict && strict[1]) return strict[1];
  const loose = compact.match(/([A-Z]{0,4}\d{4,14})/);
  if (loose && loose[1]) return loose[1];
  return compact.slice(0, 32);
}

export function extractLicenceFieldsFromPlainText(rawText: string): {
  licence_number: string | null;
  licence_issue_date: string | null;
  licence_expiry: string | null;
} {
  const text = normalizeOcrText(rawText);
  const lines = splitOcrLines(rawText);

  const issueFrom4a = firstDateInText(readFieldValueFromNumberedLines(lines, "4a"));
  const expiryFrom4b = firstDateInText(readFieldValueFromNumberedLines(lines, "4b"));
  const issue =
    issueFrom4a ??
    extractDateNearLabel(text, /\b(?:4a|4\s*a|da)\b/i) ??
    extractDateNearLabel(text, /(issue(?:\s+date)?|date\s+of\s+issue)/i) ??
    null;
  const expiry =
    expiryFrom4b ??
    extractDateNearLabel(text, /\b(?:4b|4\s*b|48)\b/i) ??
    extractDateNearLabel(text, /(expiry|expires|date\s+of\s+expiry|valid\s+until)/i) ??
    null;
  const allDates = Array.from(
    new Set(
      Array.from(
        text.matchAll(/(\d{1,2}[\/.\-]\d{1,2}[\/.\-]\d{2,4}|\d{8}|\d{4}[\/.\-]\d{1,2}[\/.\-]\d{1,2})/g),
      )
        .map((m) => normalizeIsoDate(m[1]))
        .filter((v): v is string => Boolean(v)),
    ),
  ).sort();
  const issueResolved = issue ?? (allDates.length >= 1 ? allDates[0] : null);
  const expiryResolved = expiry ?? (allDates.length >= 2 ? allDates[allDates.length - 1] : null);

  const licenceNumber =
    extractLicenceNumberFromField5(lines, rawText) ||
    extractUk7120LicenceNumber(rawText) ||
    null;
  const verified = normalizeVerifiedTrainLicenceNumber(licenceNumber);
  return {
    licence_number: verified || (licenceNumber ? String(licenceNumber).replace(/\s+/g, "").toUpperCase() : null),
    licence_issue_date: issueResolved,
    licence_expiry: expiryResolved,
  };
}

export function sanitizeBackLicenceLanguage(raw: string): string | null {
  let s = String(raw || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return null;
  const cutMarkers =
    /\b(?:9\s*a\s*[.\u00b7]?\s*2|9a\.2|9a2|9\s*b|9b|medical\s+restriction|restrictions?\s+on\s+driving|native\s+language|other\s+national|linguistic\s+knowledge|infrastructure\s+for\s+which)\b/i;
  const cutIdx = s.search(cutMarkers);
  if (cutIdx === 0) return null;
  if (cutIdx > 0) s = s.slice(0, cutIdx).trim();
  s = s.split(/[;\n|]/)[0].trim();
  s = s
    .replace(/^(?:9\s*a\s*[.\u00b7]?\s*1|9a\.1|9a1)\s*[)\].:,\-–—]?\s*/i, "")
    .replace(/^(?:primary\s+)?(?:language|linguistic|linguistics)\s*[)\].:,\-–—]?\s*/i, "")
    .replace(/^[\s:;.,\-–—]+/, "")
    .replace(/[\s:;.,\-–—]+$/, "")
    .trim();
  s = s
    .replace(/[^A-Za-z'\-\s]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  if (!s || s.length < 2) return null;
  if (s.length > 48) s = s.slice(0, 48).trim();
  return s || null;
}

function extract9a1PrimaryLanguage(lines: string[], rawText: string): string | null {
  const inline = /^(?:9\s*a\s*[.\u00b7]?\s*1|9a\.1|9a1)\s*[)\].:,\-–—]?\s*(.+)$/i;
  const labelOnly = /^(?:9\s*a\s*[.\u00b7]?\s*1|9a\.1|9a1)\s*[)\].:,\-–—]?\s*$/i;
  const stopNext = /^(?:9\s*a\s*[.\u00b7]?\s*2|9a\.2|9a2|9\s*b|9b)\b/i;

  const cleanLanguageValue = (raw: string): string | null => {
    let t = String(raw || "")
      .replace(/\s+/g, " ")
      .replace(/^[\s:;.,\-–—]+/, "")
      .replace(/[\s:;.,\-–—]+$/, "")
      .trim();
    if (!t || t.length < 2) return null;
    t = t.replace(/\s*(?:9\s*a|9a|9\s*b|9b).*$/i, "").trim();
    if (/\b(?:medical|restriction|restrictions|native|infrastructure|linguistic|knowledge|driving)\b/i.test(t)) return null;
    t = t
      .replace(/[^A-Za-z'\-\s]/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim();
    if (t.length > 48) t = t.slice(0, 48).trim();
    if (t.split(/\s+/).length > 3) return null;
    return t || null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const m = line.match(inline);
    if (m && m[1]) {
      const v = cleanLanguageValue(m[1]);
      if (v) return v;
    }
    if (labelOnly.test(line)) {
      const parts: string[] = [];
      for (let j = i + 1; j < Math.min(lines.length, i + 5); j++) {
        const next = lines[j].trim();
        if (!next || stopNext.test(next)) break;
        parts.push(next);
      }
      const v = cleanLanguageValue(parts.join(" "));
      if (v) return v;
    }
  }

  const flat = String(rawText || "").replace(/\s+/g, " ");
  const block = flat.match(
    /\b(?:9\s*a\s*[.\u00b7]?\s*1|9a\.1|9a1)\s*[)\].:,\-–—]?\s*([^0-9\n]{2,40}?)(?=\s*(?:9\s*a\s*[.\u00b7]?\s*2|9a\.2|9a2|9\s*b|9b)\b)/i,
  );
  if (block && block[1]) {
    const v = cleanLanguageValue(block[1]);
    if (v) return v;
  }

  // Fallback: standalone language word near "English" etc. when labels are garbled.
  const langWord = flat.match(/\b(English|Welsh|Scottish Gaelic|Irish|French|German|Spanish)\b/i);
  if (langWord) return cleanLanguageValue(langWord[1]);

  return null;
}

export function extractLicenceBackFieldsFromPlainText(rawText: string): {
  licence_language: string | null;
  licence_restrictions: string | null;
} {
  const lines = splitOcrLines(rawText);
  const lang = extract9a1PrimaryLanguage(lines, rawText);
  const cleaned = lang ? sanitizeBackLicenceLanguage(lang) : null;
  const normalizeYesNo = (raw: string): boolean | null => {
    const token = String(raw || "")
      .toLowerCase()
      .replace(/[^a-z]/g, "");
    if (!token) return null;
    if (token === "yes" || token === "ves" || token === "ycs") return true;
    if (token === "no" || token === "n0") return false;
    return null;
  };
  const extractYesNoToken = (raw: string): boolean | null => {
    const direct = normalizeYesNo(raw);
    if (direct !== null) return direct;
    const m = String(raw || "").toLowerCase().match(/\b(yes|ves|ycs|no|n0)\b/);
    if (!m) return null;
    const t = m[1];
    if (t === "yes" || t === "ves" || t === "ycs") return true;
    if (t === "no" || t === "n0") return false;
    return null;
  };
  const extract9bMedicalRestrictionIsYes = (): boolean | null => {
    const label = /^(?:9\s*b|9b)\s*[)\].:,\-–—]?\s*(.*)$/i;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const m = line.match(label);
      if (!m) continue;
      const rest = (m[1] || "").trim();
      const direct = extractYesNoToken(rest);
      if (direct !== null) return direct;
      for (let j = i + 1; j < Math.min(lines.length, i + 6); j++) {
        const n = lines[j].trim();
        if (!n) break;
        if (/^(?:9\s*[a-z]|10\b|11\b)/i.test(n)) break;
        const v = extractYesNoToken(n);
        if (v !== null) return v;
      }
    }
    const strictLine = String(rawText || "").match(/^(?:\s*)(?:9\s*b|9b)\s*[)\].:,\-–—]?\s*(yes|no)\s*$/im);
    if (strictLine) return strictLine[1].toLowerCase() === "yes";
    return null;
  };
  const med = extract9bMedicalRestrictionIsYes();
  return {
    licence_language: cleaned,
    licence_restrictions: med === true ? "Yes" : med === false ? "No" : null,
  };
}

export function scoreLicenceFrontOcrText(rawText: string): number {
  const fields = extractLicenceFieldsFromPlainText(rawText);
  let score = 0;
  if (fields.licence_number) score += 40;
  if (fields.licence_issue_date) score += 20;
  if (fields.licence_expiry) score += 20;
  if (extractUk7120LicenceNumber(rawText)) score += 10;
  return score + Math.min(String(rawText || "").length, 500) / 50;
}

export function scoreLicenceBackOcrText(rawText: string): number {
  const fields = extractLicenceBackFieldsFromPlainText(rawText);
  let score = 0;
  if (fields.licence_language) score += 30;
  if (fields.licence_restrictions) score += 30;
  return score + Math.min(String(rawText || "").length, 500) / 50;
}

/** Layout markers on the photo side of a UK train driving licence (fields 1–7). */
export function scoreLicenceFrontStructuralMarkers(rawText: string): number {
  const text = String(rawText || "");
  const upper = text.toUpperCase();
  let score = 0;
  if (/TRAIN\s+DRIV(?:ING|ER)?\s+LICEN[CS]E/i.test(text)) score += 28;
  if (extractUk7120LicenceNumber(text)) score += 30;
  if (/\b(?:4a|4\s*a|4b|4\s*b|4d|4\s*d)\b/i.test(text)) score += 16;
  if (/\b(?:^|\s)(?:5|s)\s*[)\].:,-]/im.test(text)) score += 12;
  if (/\b(?:1|2|3|6|7)\s*[)\].:,-]/i.test(text)) score += 8;
  if (/SURNAME|GIVEN\s+NAME|DATE\s+AND\s+PLACE\s+OF\s+BIRTH/i.test(upper)) score += 10;
  if (/\bUNITED\s+KINGDOM\b/i.test(upper) && !/\b(?:9\s*a|9a|9\s*b|9b)\b/i.test(text)) score += 6;
  return score;
}

/** Layout markers on the reverse (fields 8–11, 9a.1 / 9b). */
export function scoreLicenceBackStructuralMarkers(rawText: string): number {
  const text = String(rawText || "");
  let score = 0;
  if (/\b(?:9\s*a\s*[.\u00b7]?\s*1|9a\.1|9a1)\b/i.test(text)) score += 32;
  if (/\b(?:9\s*a\s*[.\u00b7]?\s*2|9a\.2|9a2)\b/i.test(text)) score += 18;
  if (/\b(?:9\s*b|9b)\b/i.test(text)) score += 28;
  if (/MEDICAL\s+RESTRICTION|RESTRICTIONS?\s+ON\s+DRIVING/i.test(text)) score += 18;
  if (/INFRASTRUCTURE\s+FOR\s+WHICH|OTHER\s+NATIONAL\s+RULES/i.test(text)) score += 16;
  if (/NATIVE\s+LANGUAGE|LINGUISTIC\s+KNOWLEDGE|PRIMARY\s+LANGUAGE/i.test(text)) score += 14;
  if (/\b(?:8|10|11)\s*[)\].:,-]/i.test(text)) score += 10;
  return score;
}

export type LicenceSideClassification = {
  side: "front" | "back" | "unknown";
  frontScore: number;
  backScore: number;
  confidence: "high" | "medium" | "low";
};

/** Guess front vs back from OCR text — used to catch swapped upload slots. */
export function classifyLicenceSideFromPlainText(rawText: string): LicenceSideClassification {
  const frontScore =
    scoreLicenceFrontOcrText(rawText) + scoreLicenceFrontStructuralMarkers(rawText);
  const backScore =
    scoreLicenceBackOcrText(rawText) + scoreLicenceBackStructuralMarkers(rawText);
  const diff = Math.abs(frontScore - backScore);
  const maxScore = Math.max(frontScore, backScore);
  let confidence: LicenceSideClassification["confidence"] = "low";
  if (diff >= 28 && maxScore >= 38) confidence = "high";
  else if (diff >= 16 && maxScore >= 26) confidence = "medium";

  if (confidence === "low" || diff < 12 || maxScore < 18) {
    return { side: "unknown", frontScore, backScore, confidence };
  }
  return {
    side: frontScore > backScore ? "front" : "back",
    frontScore,
    backScore,
    confidence,
  };
}

/** When the detected side differs from the upload slot, build image URL patch (optionally swapping slots). */
export function buildLicenceSideSwapPatch(args: {
  imageDataUrl: string;
  uploadedToSide: "front" | "back";
  correctSide: "front" | "back";
  currentFrontUrl: string;
  currentBackUrl: string;
}): {
  licence_image_url?: string;
  licence_back_image_url?: string;
  swapped: boolean;
} {
  const { imageDataUrl, uploadedToSide, correctSide, currentFrontUrl, currentBackUrl } = args;
  if (uploadedToSide === correctSide) {
    return correctSide === "front"
      ? { licence_image_url: imageDataUrl, swapped: false }
      : { licence_back_image_url: imageDataUrl, swapped: false };
  }

  if (correctSide === "front") {
    const patch: {
      licence_image_url: string;
      licence_back_image_url?: string;
      swapped: boolean;
    } = { licence_image_url: imageDataUrl, swapped: true };
    if (currentFrontUrl) patch.licence_back_image_url = currentFrontUrl;
    else if (currentBackUrl && currentBackUrl !== imageDataUrl) {
      patch.licence_back_image_url = currentBackUrl;
    }
    return patch;
  }

  const patch: {
    licence_back_image_url: string;
    licence_image_url?: string;
    swapped: boolean;
  } = { licence_back_image_url: imageDataUrl, swapped: true };
  if (currentBackUrl) patch.licence_image_url = currentBackUrl;
  else if (currentFrontUrl && currentFrontUrl !== imageDataUrl) {
    patch.licence_image_url = currentFrontUrl;
  }
  return patch;
}
